import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn';

export const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary/10 text-primary',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        outline: 'text-foreground border-border',
        success: 'border-transparent bg-success/10 text-success',
        warning: 'border-transparent bg-warning/10 text-warning',
        destructive: 'border-transparent bg-destructive/10 text-destructive',
        info: 'border-transparent bg-info/10 text-info',
        muted: 'border-transparent bg-muted text-muted-foreground',
      },
    },
    defaultVariants: { variant: 'default' },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

type StatusTone = 'success' | 'warning' | 'destructive' | 'info' | 'muted' | 'default';

const statusMap: Record<string, StatusTone> = {
  active: 'success',
  running: 'success',
  up: 'success',
  published: 'success',
  validated: 'success',
  paid: 'success',
  success: 'success',
  complete: 'success',
  completed: 'success',
  ready: 'success',
  open: 'info',
  pending: 'warning',
  in_progress: 'warning',
  stopped: 'warning',
  processing: 'warning',
  failed: 'destructive',
  down: 'destructive',
  error: 'destructive',
  unpaid: 'destructive',
  rejected: 'destructive',
  cancelled: 'muted',
  canceled: 'muted',
  closed: 'muted',
  inactive: 'muted',
  orphaned: 'muted',
  shutdown: 'muted',
};

export interface StatusBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  status?: string | null;
  showDot?: boolean;
}

export function StatusBadge({ status, className, showDot = true, ...props }: StatusBadgeProps) {
  const key = (status || '').toString().toLowerCase();
  const tone = statusMap[key] || 'default';
  const label = status ? status.toString().replace(/_/g, ' ') : 'unknown';
  return (
    <Badge variant={tone} className={cn('capitalize', className)} {...props}>
      {showDot && (
        <span
          className={cn('h-1.5 w-1.5 rounded-full', {
            'bg-success': tone === 'success',
            'bg-warning animate-pulse-soft': tone === 'warning',
            'bg-destructive': tone === 'destructive',
            'bg-info': tone === 'info',
            'bg-muted-foreground': tone === 'muted',
            'bg-primary': tone === 'default',
          })}
        />
      )}
      {label}
    </Badge>
  );
}
